import { ContentRoute } from './content-route';
import { ItemTypeCategory } from '../../items/models/item-type';
import { AnswerId, AttemptId, GroupId, ItemId, ItemPath, ParticipantId } from '../ids';
import { createSelector } from '@ngrx/store';
import { fromObservation } from 'src/app/store/observation';
import { isUser } from './group-route';

/* **********************************************************************************************************
 * Item Route: Object storing information required to navigate to an item
 *
 * It exists in 3 formats depending on how fully-defined it is:
 * - `FullItemRoute` contains all required info (path and (self or parent) attempt) so that no more queries are required after navigation
 * - `ItemRoute` may miss the (self and parent) attempt, will require fetching results to determine attempt after navigation
 * - `RawItemRoute` may miss path and attempt (will require fetching the path and attempts after navigation). Is not a `ContentRoute`
 *
 * When navigating, as many information as possible should be given to the item router so that the navigation is not slowed down.
 * So always prefer building a `FullItemRoute` over a `ItemRoute` over a `RawItemRoute` (if you have the info)
 * ********************************************************************************************************** */

// STRUCTURES
export interface ItemRoute extends ContentRoute {
  contentType: ItemTypeCategory,
  id: ItemId,
  path: ItemPath,
  attemptId?: AttemptId,
  parentAttemptId?: AttemptId,
  answer?: { id: AnswerId, best?: undefined } | { best: { id?: ParticipantId /* not set -> mine */ }, id?: undefined },
  observedGroup?: { id: GroupId, isUser: boolean },
}
export type FullItemRoute = ItemRoute & (Required<Pick<ItemRoute, 'attemptId'>> | Required<Pick<ItemRoute, 'parentAttemptId'>>);
export type RawItemRoute = Omit<ItemRoute, 'path'> & Partial<Pick<ItemRoute, 'path'>>;

// TYPE ASSERT FUNCTIONS
export function isItemRoute(route: ContentRoute): route is ItemRoute {
  return ([ 'activity', 'skill' ].includes(route.contentType));
}

export function isFullItemRoute(route: ContentRoute): route is FullItemRoute {
  return isItemRoute(route) && (!!route.attemptId || !!route.parentAttemptId);
}

export function isRouteWithSelfAttempt(item: FullItemRoute): item is ItemRoute & Required<Pick<ItemRoute, 'attemptId'>> {
  return item.attemptId !== undefined;
}

// FACTORIES
export function itemRoute(contentType: ItemTypeCategory, id: ItemId, attrs: Omit<FullItemRoute, 'contentType'|'id'>): FullItemRoute;
export function itemRoute(contentType: ItemTypeCategory, id: ItemId, attrs: Omit<ItemRoute, 'contentType'|'id'>): ItemRoute;
export function itemRoute(contentType: ItemTypeCategory, id: ItemId, attrs?: Omit<RawItemRoute, 'contentType'|'id'>): RawItemRoute;

export function itemRoute(contentType: ItemTypeCategory, id: ItemId, attrs?: Omit<RawItemRoute, 'contentType'|'id'>): RawItemRoute {
  return { ...attrs, contentType, id };
}

export function itemRouteWith<T extends RawItemRoute, U extends T>(route: T, attrs: Partial<ItemRoute>): U {
  return { ...route, ...attrs } as U;
}


/**
 * Add to the given route, the given self attempt id (if any) (used when only the parent id was know until now)
 */
export function routeWithSelfAttempt(route: FullItemRoute, attemptId: string|undefined): FullItemRoute {
  return isRouteWithSelfAttempt(route) ? route : { ...route, attemptId };
}

/**
 * Remove the attempt from the given route and use the given parent attempt id
 */
export function itemRouteUsingParentAttempt<T extends RawItemRoute, U extends T>(route: T, parentAttemptId: string|undefined): U {
  return { ...route, attemptId: undefined, parentAttemptId } as U;
}

export function routeWithNoObservation(route: FullItemRoute): FullItemRoute {
  // when leaving observation, we also leave the loaded answer if any
  return { ...route, observedGroup: undefined, answer: undefined };
}

/**
 * Return the route of the parent item of the given item route.
 * Beware it assumes the parent has the same content type as its child... which is not fully guaranteed
 */
export function parentRoute(route: ItemRoute, defaultActivityRoute: FullItemRoute): ItemRoute {
  if (route.path.length === 0) return defaultActivityRoute;
  return itemRoute(route.contentType, route.path[route.path.length - 1]!, {
    attemptId: route.parentAttemptId ?? undefined,
    path: route.path.slice(0, -1),
  });
}

/**
 * Selector to get the currently observed group as an item route parameter
 */
export const selectObservedGroupRouteAsItemRouteParameter = createSelector(
  fromObservation.selectObservedGroupRoute,
  route => (route !== null ? { observedGroup: { id: route.id, isUser: isUser(route) } } : {})
);
